import { HttpClient } from '@angular/common/http';
import { afterNextRender, inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, ExtraOptions, Route, Router, RouterStateSnapshot, Routes } from '@angular/router';
import { lastValueFrom, } from 'rxjs';
import { ProductCardModelSpecificationGroupSearchProductResponse } from 'src/business/openapi';
import { SearchApiMap } from 'src/business/search';
import { GA4 } from 'src/common/ga4.model';
import { Page } from 'src/common/models';
import { origin } from 'src/utils/origin';
import { ContentCacheUtils } from 'src/utils/storage.util';
import { UrlUtil } from 'src/utils/url.util';


export type ContentMetadata = {
  [key: string]: string;
}

declare global {
  interface Window { __CONTENT_CACHE: { [key: string]: any }; prerenderReady: boolean }
}

export class RouteConfig {
  static isFirst: boolean = true;
  static pages: { [key: string]: (path: string) => Route } = {
    notFoundPage: (path: string) => ({
      path,
      pathMatch: 'full',
      runGuardsAndResolvers: 'always',
      resolve: { content: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(UnknownContentResolver).resolve(route, state) },
      loadComponent: () => import('../content/content.component').then(m => m.ContentComponent)
    }),
    contentPage: (path: string) => ({
      path,
      pathMatch: 'full',
      runGuardsAndResolvers: 'always',
      resolve: { content: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(ContentResolver).resolve(route, state) },
      loadComponent: () => import('../content/content.component').then(m => m.ContentComponent)
    }),
    homePage: (path: string) => ({
      path,
      pathMatch: 'full',
      runGuardsAndResolvers: 'always',
      resolve: { content: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(ContentResolver).resolve(route, state) },
      loadComponent: () => import('../content/home.component').then(m => m.HomeComponent)
    }),
    articleOverview: (path: string) => ({
      path,
      pathMatch: 'full',
      runGuardsAndResolvers: 'always',
      resolve: { content: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(ContentResolver).resolve(route, state) },
      loadComponent: () => import('../content/article-overview.component').then(m => m.ArticleOverviewComponent)
    }),
    articlePage: (path: string) => ({
      path,
      pathMatch: 'full',
      runGuardsAndResolvers: 'always',
      resolve: { content: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(ContentResolver).resolve(route, state) },
      loadComponent: () => import('../content/article.component').then(m => m.ArticleComponent)
    }),
    searchPage: (path: string) => ({
      path,
      pathMatch: 'full',
      runGuardsAndResolvers: 'always',
      resolve: { content: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(CatalogContentAndSearchResolver).resolve(route, state) },
      loadComponent: () => import('../product/product-list.component').then(m => m.ProductListComponent)
    }),
    catalogPage: (path: string) => ({
      path,
      pathMatch: 'full',
      runGuardsAndResolvers: 'always',
      resolve: { content: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(CatalogContentAndSearchResolver).resolve(route, state) },
      loadComponent: () => import('../product/product-list.component').then(m => m.ProductListComponent)
    }),
    productPage: (path: string) => ({
      path: `${path.split('/').slice(0, -2).join('/')}/:id/:title`,
      pathMatch: 'full',
      runGuardsAndResolvers: 'always',
      resolve: { content: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(ContentResolver).resolve(route, state) },
      loadComponent: () => import('../product/product-detail.component').then(m => m.ProductDetailComponent)
    }),

    basketPage: (path: string) => ({
      path,
      pathMatch: 'full',
      runGuardsAndResolvers: 'always',
      resolve: { content: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(ContentResolver).resolve(route, state) },
      loadComponent: () => import('../ecommerce/basket.component').then(m => m.BasketComponent)
    }),
    checkoutPage: (path: string) => ({
      path,
      pathMatch: 'full',
      runGuardsAndResolvers: 'always',
      resolve: { content: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(ContentResolver).resolve(route, state) },
      loadComponent: () => import('../ecommerce/checkout.component').then(m => m.CheckoutComponent)
    }),
    receiptPage: (path: string) => ({
      path,
      pathMatch: 'full',
      runGuardsAndResolvers: 'always',
      resolve: { content: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(ContentResolver).resolve(route, state) },
      loadComponent: () => import('../ecommerce/order.component').then(m => m.OrderComponent),
    }),
    storePage: (path: string) => ({
      path,
      pathMatch: 'full',
      runGuardsAndResolvers: 'always',
      resolve: { content: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(ContentResolver).resolve(route, state) },
      loadComponent: () => import('../store/store.component').then(m => m.StoreComponent),
    }),
    storeOverviewPage: (path: string) => ({
      path,
      pathMatch: 'full',
      runGuardsAndResolvers: 'always',
      resolve: { content: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(ContentResolver).resolve(route, state) },
      loadComponent: () => import('../store/store-overview.component').then(m => m.StoreOverviewComponent),
    }),
  }

  static routerOptions: ExtraOptions = {
    onSameUrlNavigation: 'reload',
    scrollPositionRestoration: 'enabled',
    // useHash: true,
  };

  static fixUrl(params: string) {
    if (params.startsWith('/'))
      params = params.replace('/', '');
    if (params.endsWith('/'))
      params = params.slice(0, -1);
    return params;
  }
  static routes() {
    // let lang = (globalThis?.document.body.parentNode as HTMLElement)?.lang;
    let _routes: { [key: string]: string } = ContentCacheUtils.routing();
    let routeEnterties = Object.entries(_routes || {});

    let givenRoutes = routeEnterties.map(
      ([url, alias]) => alias in RouteConfig.pages ?
        RouteConfig.pages[alias](RouteConfig.fixUrl(url)) :
        RouteConfig.pages['notFoundPage'](RouteConfig.fixUrl(url))
    );

    let knownRoutes = [
      ...givenRoutes,
      RouteConfig.pages['productPage'](':market/product/:id/:title'),
      RouteConfig.pages['productPage']('product/:id/:title'),
      RouteConfig.pages['notFoundPage']('**'),
    ];
    return knownRoutes as Routes;
  }
}


function mapPageTitle(page?: any): string {
  const metaName = page?._meta?.name;
  const pageTitle = page?.pageTitle;
  return pageTitle || metaName || '';
}

function mapPageMeta(data: ContentMetadata) {
  if (!globalThis?.document)
    return []
  globalThis?.document.head.querySelectorAll('meta[dynamictag]')?.forEach(e => e.remove());
  if (data == null)
    return [];
  return Object.keys(data)
    .map(tag => ({
      name: tag,
      property: tag,
      content: data[tag]
    }))
}

function setCanonical(canonical: string) {
  if (globalThis?.document)
    globalThis?.document.head.querySelector('link[rel="canonical"]')?.setAttribute('href', canonical)
}

function prerender() {
  if (globalThis != null)
    // @ts-ignore
    if (!globalThis['prerenderReady']) {
      // give the page a little extra time to draw if external requests are slow
      setTimeout(() => {
        try {
          let _s = globalThis?.document.createElement('script');
          _s.innerHTML = "window['prerenderReady'] = true;";
          _s.type = "text/javascript";
          globalThis?.document.head.append(_s);

        } catch (error) {
          console.error('prerenderReady Falied');
        }
      }, 5000);
    }
}

interface GridLayout { layout?: GridLayout[], alias: string }
@Injectable({
  providedIn: 'root'
})
export class ContentResolver {
  protected _updaters = [
    (page?: Page) => setCanonical(page?._meta?.canonical ?? ''),
    (page?: Page) => this.title.setTitle(mapPageTitle(page)),
    (page?: Page) => mapPageMeta(page?.htmlSeoMetaTags ?? {}).forEach(tag => this.meta.addTag(tag)),
    (page?: Page) => globalThis?.document ? globalThis?.document.querySelectorAll('dialog').forEach(_d => _d.close()) : null,
    (page?: Page) => prerender(),
    (page?: Page) => GA4.track({ __event_name: 'virtual_pageview', url: page?._meta.url ?? '' } as GA4.VirtualPageview),
    (page?: Page) => globalThis?.document ? globalThis?.document.querySelectorAll<HTMLDialogElement>('dialog[open]').forEach(d => d.close()) : null,
    (page?: Page) => globalThis?.document ? globalThis?.document.querySelectorAll<HTMLElement>(':popover-open').forEach(d => d.hidePopover()) : null,
  ];

  constructor(
    protected title: Title,
    protected meta: Meta,
    protected router: Router,
    protected httpClient: HttpClient,
  ) {}

  async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<any> {
    let page;
    if (RouteConfig.isFirst) {
      RouteConfig.isFirst = false;
      page = ContentCacheUtils.page<unknown>();
    } else if (globalThis?.location) {
      // console.debug('this.router.url', this.router.url, route.url, state.url)
      let _url = new URL(state.url, origin());
      _url.searchParams.set('type', 'json');
      let _json = await lastValueFrom(this.httpClient.get(_url.href));
      // if (!response.ok)
      //   return { page: { _meta: { alias: 'notFoundPage' } } }
      // let _json = await (response).json();

      page = _json;
    }
    if (page)
      this._updaters.forEach(updater => updater(page));
    return { page };
  }
}

export type SearchResult = Partial<ProductCardModelSpecificationGroupSearchProductResponse> & { state?: string };

@Injectable({
  providedIn: 'root'
})
export class CatalogContentAndSearchResolver extends ContentResolver {
  constructor(
    title: Title,
    meta: Meta,
    _router: Router,
    httpClient: HttpClient,
  ) {
    super(title, meta, _router, httpClient);
  }

  async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<any> {
    let page = (await super.resolve(route, state))?.page;
    let tags = page?._meta?.alias === "catalogPage" ? (page.filters as { productTags: string[] }[]).map(filter => filter.productTags) : [];
    let interval = page?.interval;
    let _url = new URL(state.url, origin());
    _url.searchParams.set(UrlUtil.SIZE_PREFIX, SearchApiMap.SIZE + '');
    _url.searchParams.set(UrlUtil.INTERVAL_PREFIX, interval);
    _url.searchParams.set(UrlUtil.TAGS_PREFIX, SearchApiMap.joinTags(tags));
    let result = null;
    try {
      result = await SearchApiMap.search(SearchApiMap.UrlToModel(_url, ((page as { filters: { productTags: string[] }[] })?.filters ?? []).map(fil => fil.productTags)));
    } catch {}
    return { page, result, url: _url };
  }
}

@Injectable({
  providedIn: 'root'
})
export class UnknownContentResolver extends ContentResolver {
  static isFirst = true;
  static loadAfterNextRender?: URL;
  constructor(
    private _router: Router,
    title: Title,
    meta: Meta,
    httpClient: HttpClient,
  ) {
    super(title, meta, _router, httpClient);
    afterNextRender(() => {
      console.debug(UnknownContentResolver.loadAfterNextRender)
      if (UnknownContentResolver.loadAfterNextRender) {
        let browserUrl = UnknownContentResolver.loadAfterNextRender.href?.replace(origin(), '');
        setTimeout(() => this._router.navigateByUrl(browserUrl), 200);
      }
    })
  }
  async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<any> {
    let content = await super.resolve(route, state);
    console.debug(content, UnknownContentResolver.isFirst)
    if (content?.page?._meta?.alias != 'notFoundPage') {
      let config = RouteConfig.pages[content?.page?._meta?.alias];
      if (config != null) {
        let _url = new URL(content?.page?._meta?.url, origin());
        let _link = _url.href?.replace(origin(), '').replace('/', '');
        this._router.config = [...this._router.config.slice(0, this._router.config.length - 1), config(_link), ...this._router.config.slice(this._router.config.length - 1)];
        Object.entries(route.queryParams).forEach(([k, v]) => _url.searchParams.set(k, v))
        if (route.fragment != null)
          _url.hash = route.fragment;
        let browserUrl = _url.href?.replace(origin(), '');
        setTimeout(() => this._router.navigateByUrl(browserUrl), 200);
      }
    }
    return { page: content?.page, }
  }
}


@Injectable({
  providedIn: 'root'
})
export class InitialContentResolver extends ContentResolver {
  static isFirst = true;
  constructor(
    private _router: Router,
    title: Title,
    meta: Meta,
    httpClient: HttpClient,
  ) {
    super(title, meta, _router, httpClient);
    afterNextRender(() => {
      this.router.resetConfig(RouteConfig.routes());
      setTimeout(() => {
        this.router.navigateByUrl(document.location.href.replace(origin(), ''));
      }, 1000);
    })
  }
  async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<any> {
    let content = await super.resolve(route, state);
    if (content?.page?._meta?.alias != 'notFoundPage') {
      let config = RouteConfig.pages[content?.page?._meta?.alias];
      if (config != null && !UnknownContentResolver.isFirst) {
        let _url = new URL(content?.page?._meta?.url, origin());
        let _link = _url.href?.replace(origin(), '').replace('/', '');

        this.router.resetConfig([...this._router.config.slice(0, this._router.config.length - 1), config(_link), ...this._router.config.slice(this._router.config.length - 1)])

        Object.entries(route.queryParams).forEach(([k, v]) => _url.searchParams.set(k, v))
        if (route.fragment != null)
          _url.hash = route.fragment;
        let browserUrl = _url.href?.replace(origin(), '');
        setTimeout(() => this._router.navigateByUrl(browserUrl), 200);
      } else {
        UnknownContentResolver.isFirst = false;
      }
    }
    return { page: content?.page, }
  }

}
